import {
  getDiscoveryPlacementsForWorkerQuerySchema,
  getDiscoveryPlacementsForWorkerResponseSchema,
} from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { get } from "@src/appV2/api/parsedApi";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import {
  type QueryFunctionContext,
  type QueryKey,
  useInfiniteQuery,
  type UseInfiniteQueryOptions,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { isEmpty } from "lodash";
import { useHistory } from "react-router-dom";
import { type z } from "zod";

// Use z.output to match what parsedApi.get() returns — parsed and transformed response data.
export type DiscoveryPlacementsResponse = z.output<
  typeof getDiscoveryPlacementsForWorkerResponseSchema
>;

export function fetchDiscoveryPlacementsApiUrl(workerId: string): string {
  return `${environmentConfig.REACT_APP_BASE_API_URL}/v2/workers/${workerId}/placements`;
}

interface FetchDiscoveryPlacementsParams {
  [key: string]: unknown;
  workerId: string;
  placementCandidateId: string;
  filter?: {
    [key: string]: unknown;
    workerTypes?: string;
    jobTypes?: string;
    shiftTypes?: string;
    daysOfWeek?: string;
    state?: string;
    tags?: string;
    matchedType?: string;
    newGradFriendly?: boolean;
    workplaceId?: string;
  };
  workerTypesAvailableForHiring?: string[];
}

export function mapFilterToDiscoveryQuery(
  filter: FetchDiscoveryPlacementsParams["filter"]
): Record<string, unknown> {
  if (!filter) {
    return {};
  }

  const mapped: Record<string, unknown> = {};

  // useGetPlacementFilters outputs distance as "distanceInMiles.$lt": number (flat key).
  // Map it to the discovery API's distanceInMiles (flat number).
  const distanceValue = filter["distanceInMiles.$lt"];
  if (isDefined(distanceValue)) {
    mapped.distanceInMiles = distanceValue;
  }

  if (isDefined(filter.workerTypes)) {
    mapped.workerTypes = filter.workerTypes;
  }
  if (isDefined(filter.jobTypes)) {
    mapped.jobTypes = filter.jobTypes;
  }
  if (isDefined(filter.shiftTypes)) {
    mapped.shiftTypes = filter.shiftTypes;
  }
  if (isDefined(filter.daysOfWeek)) {
    mapped.daysOfWeek = filter.daysOfWeek;
  }
  if (isDefined(filter.state)) {
    mapped.state = filter.state;
  }
  if (isDefined(filter.tags)) {
    mapped.tags = filter.tags;
  }
  if (isDefined(filter.matchedType)) {
    mapped.matchedType = filter.matchedType;
  }
  if (isDefined(filter.newGradFriendly)) {
    mapped.newGradFriendly = filter.newGradFriendly;
  }
  if (isDefined(filter.workplaceId)) {
    mapped.workplaceIds = [filter.workplaceId];
  }

  return mapped;
}

export function useFetchDiscoveryPlacements(
  params: FetchDiscoveryPlacementsParams,
  options: UseInfiniteQueryOptions<DiscoveryPlacementsResponse, AxiosError> = {}
) {
  const { workerId, placementCandidateId, filter, workerTypesAvailableForHiring } = params;
  const history = useHistory();
  const searchParams = new URLSearchParams(history.location.search);
  const source = searchParams.get("source");

  return useInfiniteQuery({
    queryKey: [fetchDiscoveryPlacementsApiUrl(workerId), filter, source],
    keepPreviousData: true,
    queryFn: async ({
      pageParam: pageParameter,
    }: QueryFunctionContext<QueryKey, string | undefined>) => {
      const mappedFilter = mapFilterToDiscoveryQuery(filter);
      const nonEmptyFilter = Object.fromEntries(
        Object.entries(mappedFilter).filter(([_, value]) => isDefined(value))
      );

      const response = await get({
        url: fetchDiscoveryPlacementsApiUrl(workerId),
        queryParamsSchema: getDiscoveryPlacementsForWorkerQuerySchema,
        queryParams: {
          page: {
            size: !isDefined(pageParameter) ? 5 : 15,
            cursor: pageParameter,
          },
          filter: nonEmptyFilter,
        },
        responseSchema: getDiscoveryPlacementsForWorkerResponseSchema,
      });

      if (!isEmpty(placementCandidateId)) {
        logEvent(APP_V2_APP_EVENTS.PLACEMENTS_PAGE_VIEWED, {
          placementCandidateId,
          filter: nonEmptyFilter,
          source,
          ...(isDefined(workerTypesAvailableForHiring) &&
            workerTypesAvailableForHiring.length > 0 && {
              workerTypesAvailableForHiring: workerTypesAvailableForHiring.join(","),
            }),
        });
      }

      // Client-side sort: prioritize placements with valid document requirements
      const sortedData = response.data.data.toSorted((a, b) => {
        if (a.meta.isDocumentRequirementValid && !b.meta.isDocumentRequirementValid) {
          return -1;
        }

        if (!a.meta.isDocumentRequirementValid && b.meta.isDocumentRequirementValid) {
          return 1;
        }

        return 0;
      });

      return { ...response.data, data: sortedData };
    },
    getNextPageParam: (lastPage) => lastPage.links.nextCursor ?? undefined,
    ...options,
  });
}
